import React, { useState } from 'react';
import { string, func } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import css from './ScheduledPublishPicker.module.css';

/**
 * ScheduledPublishPicker — datetime picker for scheduling listing publication.
 *
 * Shows in the EditListingWizard publish step when scheduledPublish feature is ON.
 * Lets the user pick a future date/time to auto-publish their draft listing.
 *
 * Usage:
 *   {features.scheduledPublish && (
 *     <ScheduledPublishPicker
 *       listingId={listing.id.uuid}
 *       currentSchedule={listing.attributes.publicData.scheduledPublishAt}
 *       onScheduled={date => console.log('Scheduled for', date)}
 *     />
 *   )}
 */
const ScheduledPublishPicker = ({ listingId, currentSchedule, onScheduled, className }) => {
  // Convert ISO string to datetime-local value
  const toLocal = iso => {
    if (!iso) return '';
    const d = new Date(iso);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
  };

  const [value, setValue] = useState(toLocal(currentSchedule));
  const [isLoading, setIsLoading] = useState(false);
  const [saved, setSaved] = useState(!!currentSchedule);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    if (!value) return;
    const isoDate = new Date(value).toISOString();

    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/scheduled-publish/${listingId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scheduledAt: isoDate }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.error || 'Failed to schedule');
      }
      setSaved(true);
      onScheduled && onScheduled(isoDate);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = async () => {
    setIsLoading(true);
    try {
      await fetch(`/api/scheduled-publish/${listingId}`, { method: 'DELETE' });
      setValue('');
      setSaved(false);
      onScheduled && onScheduled(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Min datetime = 5 minutes from now
  const minDate = new Date(Date.now() + 5 * 60000)
    .toISOString()
    .slice(0, 16);

  return (
    <div className={classNames(css.root, className)}>
      <label className={css.label}>
        <FormattedMessage id="ScheduledPublishPicker.label" />
      </label>
      <p className={css.description}>
        <FormattedMessage id="ScheduledPublishPicker.description" />
      </p>
      <div className={css.row}>
        <input
          type="datetime-local"
          value={value}
          min={minDate}
          onChange={e => { setValue(e.target.value); setSaved(false); }}
          className={css.input}
          disabled={isLoading}
        />
        {value && !saved && (
          <button
            type="button"
            onClick={handleSave}
            disabled={isLoading}
            className={css.saveBtn}
          >
            <FormattedMessage id="ScheduledPublishPicker.save" />
          </button>
        )}
        {saved && (
          <button
            type="button"
            onClick={handleClear}
            disabled={isLoading}
            className={css.clearBtn}
          >
            <FormattedMessage id="ScheduledPublishPicker.clear" />
          </button>
        )}
      </div>
      {saved && (
        <p className={css.savedMsg}>
          <FormattedMessage id="ScheduledPublishPicker.scheduled" values={{ date: new Date(value).toLocaleString() }} />
        </p>
      )}
      {error && <p className={css.error}>{error}</p>}
    </div>
  );
};

ScheduledPublishPicker.defaultProps = {
  currentSchedule: null,
  onScheduled: null,
  className: null,
};
ScheduledPublishPicker.propTypes = {
  listingId: string.isRequired,
  currentSchedule: string,
  onScheduled: func,
  className: string,
};

export default ScheduledPublishPicker;
